// /api/me probe — returns the current user when authenticated,
// 401 when not. The backend serves this even with auth disabled
// (`authDisabled: true`) so the frontend can branch on whether to
// show the login screen or a signed-in chrome.

import type { UpstreamSnapshot } from "../upstream-auth/api";

export interface Me {
  email: string;
  authDisabled: boolean;
  /** True when the backend has the OAuth env configured so the
   *  operator can flip auth back on via /api/auth-toggle. False when
   *  there's literally no Google client to redirect to. */
  canEnableAuth: boolean;
  /** Boot-time snapshot of the upstream orchestrator's state +
   *  current token expiry. Live updates flow through the /api/events
   *  WS into the UpstreamProvider store; the value carried in /api/me
   *  is just the seed for the initial render. */
  upstream: UpstreamSnapshot;
}

export interface UnauthenticatedMe {
  authenticated: false;
}
export interface AuthenticatedMe {
  authenticated: true;
  me: Me;
}
export type MeResult = AuthenticatedMe | UnauthenticatedMe;

export async function fetchMe(): Promise<MeResult> {
  let res: Response;
  try {
    res = await fetch("/api/me", { credentials: "same-origin" });
  } catch {
    // Backend down (e.g. dev SPA running without `nix run .#start`).
    // Render the login screen rather than a blank page — clicking
    // "Sign in" will surface the underlying connection error in a
    // way the user can act on.
    return { authenticated: false };
  }
  if (res.status === 401) return { authenticated: false };
  if (!res.ok) return { authenticated: false };
  try {
    const me = (await res.json()) as Me;
    return { authenticated: true, me };
  } catch {
    return { authenticated: false };
  }
}

// Top-level navigation away from the SPA — `/auth/login` is a 302
// chain to Google and back, so we can't just fetch it.
export function startLogin(): void {
  const next = window.location.pathname + window.location.hash;
  const url = new URL("/auth/login", window.location.origin);
  url.searchParams.set("next", next || "/");
  window.location.assign(url.toString());
}

// POST (not a navigation): a GET logout would be CSRF-able via a
// cross-site link since SameSite=Lax sends the cookie on top-level GETs.
export async function startLogout(): Promise<void> {
  // Navigate home regardless — with the backend down there's no
  // session to clear anyway, and reloading lands on the login screen.
  try {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin", redirect: "manual" });
  } catch {}
  window.location.assign("/");
}

export interface ToggleAuthResult {
  authDisabled: boolean;
  canEnableAuth: boolean;
}

export async function toggleAuth(action: "enable" | "disable"): Promise<ToggleAuthResult | { error: string }> {
  const r = await fetch("/api/auth-toggle", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ action }),
  });
  if (r.ok) return (await r.json()) as ToggleAuthResult;
  try { return (await r.json()) as { error: string }; }
  catch { return { error: `request failed (${r.status})` }; }
}
